/**
 * Community socials mobile dots + scroll sync.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  function getCells(grid) {
    return Array.from(grid.querySelectorAll(':scope > .community-socials__cell')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement}
   */
  function ensureDots(root) {
    let dots = root.querySelector(':scope > .community-socials__dots');
    if (!(dots instanceof HTMLElement)) {
      dots = document.createElement('nav');
      dots.className = 'community-socials__dots';
      dots.setAttribute('aria-label', 'Community images');
      root.appendChild(dots);
    }
    return dots;
  }

  /**
   * @param {HTMLElement} grid
   * @param {HTMLElement[]} cells
   * @returns {number}
   */
  function getActiveIndex(scroller, cells) {
    if (!cells.length) return 0;
    const left = scroller.scrollLeft;
    let active = 0;
    let distance = Infinity;

    cells.forEach((cell, idx) => {
      const delta = Math.abs(cell.offsetLeft - left);
      if (delta < distance) {
        distance = delta;
        active = idx;
      }
    });

    return active;
  }

  /**
   * @param {HTMLElement} dots
   * @param {number} active
   */
  function setActiveDot(dots, active) {
    dots.querySelectorAll('.community-socials__dot').forEach((dot, idx) => {
      const on = idx === active;
      dot.classList.toggle('is-active', on);
      if (on) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initSection(root) {
    const viewport = root.querySelector('.community-socials__viewport');
    const grid = root.querySelector('.community-socials__grid');
    if (!(grid instanceof HTMLElement)) return;

    const scroller = viewport instanceof HTMLElement ? viewport : grid;

    const dots = ensureDots(root);
    const cells = getCells(grid);

    initVideoVisibility(root);
    initVideoExpand(root);

    dots.replaceChildren();

    if (!MOBILE_MQ.matches || cells.length <= 1) {
      dots.style.display = 'none';
      return;
    }

    dots.style.display = '';

    cells.forEach((cell, idx) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'community-socials__dot';
      dot.setAttribute('aria-label', `Go to image ${idx + 1}`);
      dot.addEventListener('click', () => {
        scroller.scrollTo({ left: cell.offsetLeft, behavior: 'smooth' });
      });
      dots.appendChild(dot);
    });

    const update = () => setActiveDot(dots, getActiveIndex(scroller, cells));
    update();

    if (!scroller.dataset.csDotsBound) {
      scroller.dataset.csDotsBound = 'true';
      scroller.addEventListener('scroll', update, { passive: true });
    }
  }

  /**
   * Play videos only when visible in viewport.
   * @param {HTMLElement} root
   */
  function initVideoVisibility(root) {
    if (root.dataset.csVideosBound === 'true') return;

    const videos = Array.from(root.querySelectorAll('.community-socials__video')).filter(
      (el) => el instanceof HTMLVideoElement
    );
    if (!videos.length) {
      root.dataset.csVideosBound = 'true';
      return;
    }

    // Fallback for very old browsers: keep videos running.
    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
      root.dataset.csVideosBound = 'true';
      return;
    }

    const VISIBILITY_THRESHOLD = 0.55;
    const visibilityByVideo = new Map();

    /**
     * Keep only the most visible video playing.
     */
    function syncPlayback() {
      if (root.dataset.csModalOpen === 'true') {
        videos.forEach((video) => video.pause());
        return;
      }

      /** @type {HTMLVideoElement | null} */
      let candidate = null;
      let highestRatio = VISIBILITY_THRESHOLD;

      videos.forEach((video) => {
        const ratio = visibilityByVideo.get(video) || 0;
        if (ratio >= highestRatio) {
          highestRatio = ratio;
          candidate = video;
        }
      });

      videos.forEach((video) => {
        if (video === candidate && !document.hidden) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
          return;
        }
        video.pause();
      });
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          if (entry.isIntersecting) {
            visibilityByVideo.set(video, entry.intersectionRatio);
          } else {
            visibilityByVideo.set(video, 0);
          }
        });
        syncPlayback();
      },
      {
        threshold: [0, VISIBILITY_THRESHOLD, 1]
      }
    );

    videos.forEach((video) => {
      video.preload = 'none';
      video.pause();
      visibilityByVideo.set(video, 0);
      observer.observe(video);
    });

    // Pause all section videos when tab is backgrounded.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        videos.forEach((video) => video.pause());
        return;
      }
      syncPlayback();
    });

    root.addEventListener('cs:modal-closed', syncPlayback);

    root.dataset.csVideosBound = 'true';
  }

  /**
   * Click to expand section videos in a modal dialog.
   * @param {HTMLElement} root
   */
  function initVideoExpand(root) {
    if (root.dataset.csVideoExpandBound === 'true') return;

    const dialog = root.querySelector('[data-cs-video-modal]');
    const content = root.querySelector('[data-cs-video-modal-content]');
    const closeButton = root.querySelector('[data-cs-video-close]');
    if (!(dialog instanceof HTMLDialogElement) || !(content instanceof HTMLElement)) {
      root.dataset.csVideoExpandBound = 'true';
      return;
    }

    const videos = Array.from(root.querySelectorAll('.community-socials__video')).filter(
      (el) => el instanceof HTMLVideoElement
    );

    if (!videos.length) {
      root.dataset.csVideoExpandBound = 'true';
      return;
    }

    /**
     * @param {HTMLVideoElement} sourceVideo
     */
    function openModal(sourceVideo) {
      root.dataset.csModalOpen = 'true';

      videos.forEach((video) => video.pause());

      const expandedVideo = sourceVideo.cloneNode(true);
      if (!(expandedVideo instanceof HTMLVideoElement)) return;
      expandedVideo.classList.add('community-socials__video--expanded');
      expandedVideo.controls = true;
      expandedVideo.muted = true;
      expandedVideo.preload = 'metadata';
      expandedVideo.setAttribute('playsinline', '');

      try {
        expandedVideo.currentTime = sourceVideo.currentTime || 0;
      } catch (_) {
        // Ignore seek errors if metadata isn't ready.
      }

      content.replaceChildren(expandedVideo);

      if (!dialog.open) {
        dialog.showModal();
      }

      const playPromise = expandedVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    function closeModal() {
      if (dialog.open) {
        dialog.close();
      }
    }

    videos.forEach((video, idx) => {
      if (video.dataset.csExpandBound === 'true') return;
      video.dataset.csExpandBound = 'true';
      video.setAttribute('tabindex', '0');
      video.setAttribute('role', 'button');
      video.setAttribute('aria-label', `Expand community video ${idx + 1}`);

      video.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        openModal(video);
      });

      video.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openModal(video);
      });
    });

    if (closeButton instanceof HTMLElement) {
      closeButton.addEventListener('click', closeModal);
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) {
        closeModal();
      }
    });

    dialog.addEventListener('close', () => {
      root.dataset.csModalOpen = 'false';
      content.replaceChildren();
      root.dispatchEvent(new CustomEvent('cs:modal-closed'));
    });

    root.dataset.csVideoExpandBound = 'true';
  }

  function initAll() {
    document.querySelectorAll('.community-socials').forEach((section) => {
      if (section instanceof HTMLElement) initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('resize', initAll, { passive: true });
  MOBILE_MQ.addEventListener('change', initAll);
})();
